import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Archive, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getActiveIncidents, getAllIncidents, archiveIncident, deleteIncident } from '../lib/blacklistService';
import BlacklistIncidentForm from './BlacklistIncidentForm';
import SearchCounter from './SearchCounter';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { matchesSearchTerm } from '../lib/searchUtils';

const BlacklistTab = () => {
  const { userRole } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [clientsData, setClientsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);

  useEffect(() => {
    fetchIncidents();
  }, [showArchived]);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      setError('');
      const data = showArchived ? await getAllIncidents() : await getActiveIncidents();
      setIncidents(data);
      
      // Load client data for all incidents
      await loadClientsData(data);
    } catch (error) {
      console.error('Error fetching blacklist incidents:', error);
      setError('Error al cargar los incidentes de lista negra');
    } finally {
      setLoading(false);
    }
  };

  const loadClientsData = async (incidents) => {
    try {
      const clientIds = [...new Set(incidents.map(incident => incident.clienteId).filter(Boolean))];
      const clientsMap = {};

      // Load client data for each unique client ID
      await Promise.all(
        clientIds.map(async (clientId) => {
          try {
            const clientDoc = await getDoc(doc(db, 'clients', clientId));
            if (clientDoc.exists()) {
              clientsMap[clientId] = { id: clientDoc.id, ...clientDoc.data() };
            }
          } catch (error) {
            console.error(`Error loading client ${clientId}:`, error);
            // Continue with next client if one fails
          }
        })
      );

      setClientsData(clientsMap);
    } catch (error) {
      console.error('Error loading clients data:', error);
      // Continue without client data if it fails
    }
  };

  const canManageBlacklist = () => {
    return userRole === 'admin';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const getClientDisplayName = (incident) => {
    const client = clientsData[incident.clienteId];
    
    if (!client) {
      return incident.clienteNombre || 'Cliente desconocido';
    }
    
    if (client.nombreEmpresa) {
      const principal = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
      return principal?.nombre ? `${client.nombreEmpresa} - ${principal.nombre}` : client.nombreEmpresa;
    }
    
    const principal = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
    return principal?.nombre || 'Cliente';
  };

  const handleFormSuccess = () => {
    const message = editingIncident ? 'Incidente actualizado exitosamente' : 'Incidente creado exitosamente';
    setSuccess(message);
    setEditingIncident(null);
    fetchIncidents();
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleEdit = (incident) => {
    setEditingIncident(incident);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingIncident(null);
  };

  const handleArchive = async (incident) => {
    if (!window.confirm(`¿Está seguro de archivar este incidente?`)) return;

    try {
      await archiveIncident(incident.id);
      setSuccess('Incidente archivado exitosamente');
      fetchIncidents();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving incident:', error);
      setError('Error al archivar el incidente');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (incident) => {
    if (!window.confirm(`¿Está seguro de eliminar permanentemente este incidente?\\n\\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await deleteIncident(incident.id);
      setSuccess('Incidente eliminado permanentemente');
      fetchIncidents();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting incident:', error);
      setError('Error al eliminar el incidente');
      setTimeout(() => setError(''), 5000);
    }
  };

  const filteredIncidents = incidents.filter(incident => {
    if (!searchTerm) return true;
    
    return (
      (incident.motivoIncidente && matchesSearchTerm(incident.motivoIncidente, searchTerm)) ||
      (incident.detallesIncidente && matchesSearchTerm(incident.detallesIncidente, searchTerm)) ||
      (incident.ordenFolio && matchesSearchTerm(incident.ordenFolio, searchTerm)) ||
      (incident.clienteNombre && matchesSearchTerm(incident.clienteNombre, searchTerm)) ||
      (getClientDisplayName(incident) && matchesSearchTerm(getClientDisplayName(incident), searchTerm))
    );
  });

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Lista Negra</h2>
        </div>
        {canManageBlacklist() && (
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center justify-center w-10 h-10 bg-red-600 dark:bg-red-500 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
            title="Nuevo Incidente de Lista Negra"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Bar and Filters */}
      <div className="mb-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-4">
            {success}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Buscar incidentes por motivo, detalles, folio o cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
            <SearchCounter 
              count={filteredIncidents.length} 
              label="incidentes" 
              isSearching={!!searchTerm} 
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showArchived"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 text-red-600 dark:text-red-400 focus:ring-red-500 dark:focus:ring-red-400 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
            <label htmlFor="showArchived" className="ml-2 block text-sm text-gray-900 dark:text-white">
              Mostrar archivados
            </label>
          </div>
        </div>
      </div>

      {/* Incidents List */}
      <div>
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando incidentes...
          </div>
        ) : filteredIncidents.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron incidentes que coincidan con la búsqueda' : 'No hay incidentes de lista negra registrados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Cliente/Orden
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Motivo
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Fecha
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Deuda
                  </th>
                  {canManageBlacklist() && (
                    <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredIncidents.map((incident) => (
                  <tr 
                    key={incident.id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => window.location.href = `/clients/blacklist/${incident.id}`}
                  >
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      <div className="min-w-0">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                          <div className="truncate">
                            <div className="font-semibold text-gray-900 dark:text-white">{getClientDisplayName(incident)}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">Orden: {incident.ordenFolio}</div>
                            {incident.estado === 'archivado' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 mt-1">
                                Archivado
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Show additional info on mobile */}
                        <div className="md:hidden mt-2 space-y-1">
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Motivo:</span> <span className="text-gray-600 dark:text-gray-400">{incident.motivoIncidente}</span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Fecha:</span> <span className="text-gray-500 dark:text-gray-400">{formatDate(incident.fechaIncidente)}</span>
                          </div>
                          {incident.montoDeuda && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium text-gray-600 dark:text-gray-400">Deuda:</span> <span className="text-gray-500 dark:text-gray-400">${incident.montoDeuda.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 dark:text-white hidden md:table-cell">
                      <div className="max-w-xs">
                        <div className="font-medium truncate text-gray-900 dark:text-white" title={incident.motivoIncidente}>
                          {incident.motivoIncidente}
                        </div>
                        {incident.detallesIncidente && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={incident.detallesIncidente}>
                            {incident.detallesIncidente}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white hidden lg:table-cell">
                      {formatDate(incident.fechaIncidente)}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white hidden lg:table-cell">
                      {incident.montoDeuda ? `$${incident.montoDeuda.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                    </td>
                    {canManageBlacklist() && (
                      <td className="px-1 py-4 text-sm font-medium">
                        <div className="flex flex-col sm:flex-row sm:space-x-1 space-y-1 sm:space-y-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleEdit(incident)}
                            className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                            title="Editar incidente"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleArchive(incident)}
                            className="p-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded"
                            title="Archivar incidente"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(incident)}
                            className="p-1 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title="Eliminar incidente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      <BlacklistIncidentForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingIncident={editingIncident}
        clientId={editingIncident?.clienteId}
        clientName={editingIncident ? getClientDisplayName(editingIncident) : null}
      />
    </div>
  );
};

export default BlacklistTab;